import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PostStatusBadge, PostTypePill } from "@/components/shared/Badges";
import { useAuth } from "@/contexts/AuthContext";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { TravelPosterCTAs, InclusionsList } from "@/components/posts/TravelPosterCTAs";
import { trackEvent } from "@/lib/analytics";
import { PartnerCTA } from "@/components/posts/PartnerCTA";
import { TrailerEmbed } from "@/components/posts/TrailerEmbed";
import { HeartButton } from "@/components/customer/HeartButton";
import { QRCodeModal } from "@/components/customer/QRCodeModal";
import OfferHeroCarousel from "@/components/posts/OfferHeroCarousel";
import { TicketCheck } from "lucide-react";
import {
    ArrowLeft,
    Archive,
    Send,
    Trash2,
    Pencil,
    CalendarDays,
    MapPin,
    Tag,
    Sparkles,
    Store,
    Globe,
    Clock,
    Share2,
} from "lucide-react";

const fmt = (iso) =>
    iso
        ? new Date(iso).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
          })
        : null;

export default function PostDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const load = async () => {
        setLoading(true);
        setNotFound(false);
        try {
            const res = await api.get(`/posts/${id}`);
            setPost(res.data);
        } catch (e) {
            const st = e?.response?.status;
            if (st === 404) {
                setNotFound(true);
            } else {
                toast.error(errorMessage(e));
                setNotFound(true);
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, [id]); // eslint-disable-line

    if (loading) {
        return (
            <div data-testid="post-detail-loading" className="space-y-4">
                <Skeleton className="h-8 w-40" />
                <Skeleton className="aspect-[16/9] w-full rounded-2xl" />
                <Skeleton className="h-24 w-full rounded-2xl" />
            </div>
        );
    }

    if (notFound || !post) {
        return (
            <div className="space-y-6" data-testid="post-detail-page">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                    data-testid="detail-back-btn"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                <div
                    className="rounded-2xl border border-neutral-200 bg-white p-10 text-center"
                    data-testid="post-detail-not-found"
                >
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl nest-gradient text-white nest-shadow">
                        <ArrowLeft size={26} className="rotate-180" />
                    </div>
                    <h1 className="mt-6 text-2xl font-bold text-neutral-900">
                        Post not available
                    </h1>
                    <p className="mx-auto mt-2 max-w-md text-sm text-neutral-500">
                        This post may have been removed, is still in draft, or
                        the link is incorrect.
                    </p>
                    <div className="mt-6">
                        <Link to="/posts">
                            <Button className="nest-gradient text-white" data-testid="detail-notfound-browse-btn">
                                Browse the feed
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const isOwner = user?.id && post.created_by === user.id;
    const isSuper = user?.role === "SUPER_ADMIN";
    const canEdit = isSuper || (isOwner && post.status !== "EXPIRED");
    const canDelete = isSuper;
    const canPublish =
        ["DRAFT", "ARCHIVED"].includes(post.status) &&
        (["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR"].includes(
            user?.role,
        ) ||
            (isOwner && user?.role !== "STAFF"));
    const canArchive =
        post.status !== "ARCHIVED" &&
        (isSuper ||
            isOwner ||
            ["FRANCHISE_OWNER", "DISTRICT_MANAGER"].includes(user?.role));

    const doPublish = async () => {
        try {
            const r = await api.post(`/posts/${id}/publish`);
            toast.success("Published.");
            setPost(r.data);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const doArchive = async () => {
        try {
            const r = await api.post(`/posts/${id}/archive`);
            toast.success("Archived.");
            setPost(r.data);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const doDelete = async () => {
        try {
            await api.delete(`/posts/${id}`);
            toast.success("Post deleted.");
            navigate("/posts");
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    // -----------------------------------------------------------------------
    // Phase 15c — OFFER-specific redesign. Non-OFFER post types (MOVIE,
    // EXHIBITION, JOB_FAIR, TRAVEL) keep the existing 2-column layout below.
    // -----------------------------------------------------------------------
    if (post.type === "OFFER") {
        return (
            <OfferDetailView
                post={post}
                onBack={() => navigate(-1)}
                onVisitShop={() => {
                    if (post.created_by) navigate(`/posts?shop_id=${post.created_by}`);
                    else navigate("/posts");
                }}
                adminActions={{
                    canEdit, canPublish, canArchive, canDelete,
                    doPublish, doArchive, doDelete,
                    onEdit: () => navigate(`/posts/new?edit=${post.id}`),
                }}
            />
        );
    }

    return (
        <div className="space-y-6" data-testid="post-detail-page">
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                data-testid="detail-back-btn"
            >
                <ArrowLeft size={16} /> Back
            </button>

            <div className="grid gap-6 lg:grid-cols-5">
                <div className="lg:col-span-3 space-y-4">
                    {post.type === "MOVIE" && post.details?.trailer_youtube_video_id && (
                        <TrailerEmbed videoId={post.details.trailer_youtube_video_id} title={post.title} />
                    )}
                    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
                        <div className="relative aspect-[4/3] w-full bg-neutral-100">
                            {post.poster_image_url ? (
                                <img
                                    src={publicAssetUrl(post.poster_image_url)}
                                    alt={post.title}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center nest-gradient text-3xl font-black text-white">
                                    OfferNest
                                </div>
                            )}
                            <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                                <PostTypePill type={post.type} />
                                <PostStatusBadge status={post.status} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4 lg:col-span-2">
                    <div className="rounded-2xl border border-neutral-200 bg-white p-6">
                        <div className="flex items-start justify-between gap-3">
                            <h1 className="text-2xl font-extrabold text-neutral-900" data-testid="detail-title">
                                {post.title}
                            </h1>
                            <HeartButton post={post} size={22} className="shrink-0 !bg-neutral-100 !px-3 !py-2" />
                        </div>
                        {post.business_name && (
                            <div className="mt-1 text-sm font-medium text-neutral-500">
                                {post.business_name}
                            </div>
                        )}
                        {post.description && (
                            <p className="mt-4 text-sm leading-relaxed text-neutral-700">
                                {post.description}
                            </p>
                        )}
                        <div className="mt-4 space-y-2 text-sm text-neutral-700">
                            <div className="flex items-center gap-2">
                                <MapPin size={14} className="text-neutral-400" />
                                <span className="capitalize">{post.district_slug}</span>
                            </div>
                            {post.category_slug && (
                                <div className="flex items-center gap-2">
                                    <Tag size={14} className="text-neutral-400" />
                                    <span className="capitalize">
                                        {post.category_slug.replace(/-/g, " ")}
                                    </span>
                                </div>
                            )}
                            {post.valid_from && (
                                <div className="flex items-center gap-2">
                                    <CalendarDays size={14} className="text-neutral-400" />
                                    From {fmt(post.valid_from)}
                                </div>
                            )}
                            {post.valid_to && (
                                <div className="flex items-center gap-2">
                                    <CalendarDays size={14} className="text-neutral-400" />
                                    Until {fmt(post.valid_to)}
                                </div>
                            )}
                        </div>

                        {post.details && Object.keys(post.details).length > 0 && (
                            <div className="mt-5 rounded-xl bg-neutral-50 p-4">
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                                    Details
                                </div>
                                <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm">
                                    {Object.entries(post.details)
                                        .filter(([k, v]) => {
                                            // Hide the travel booking fields — we render them
                                            // separately as CTAs + inclusions list.
                                            if (
                                                [
                                                    "inclusions",
                                                    "booking_cta_type",
                                                    "booking_whatsapp",
                                                    "booking_phone",
                                                    "booking_price_label",
                                                    "partner_url",
                                                    "partner_brand_slug",
                                                    "trailer_youtube_url",
                                                    "trailer_youtube_video_id",
                                                ].includes(k)
                                            )
                                                return false;
                                            if (v === null || v === undefined || v === "" || v === false) return false;
                                            return true;
                                        })
                                        .map(([k, v]) => (
                                        <div key={k}>
                                            <dt className="text-neutral-500">
                                                {k.replace(/_/g, " ")}
                                            </dt>
                                            <dd className="font-medium text-neutral-800 break-words">
                                                {Array.isArray(v)
                                                    ? v.join(", ")
                                                    : typeof v === "object" && v !== null
                                                      ? JSON.stringify(v)
                                                      : String(v ?? "")}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        <InclusionsList
                            inclusions={post.details?.inclusions}
                            className="mt-5 rounded-xl bg-emerald-50/50 p-4"
                        />

                        {post.details?.booking_cta_type && (
                            <div className="mt-4">
                                <TravelPosterCTAs post={post} />
                            </div>
                        )}

                        {post.details?.partner_url && (
                            <PartnerCTA post={post} className="mt-4" />
                        )}

                        <CouponClaimBlock post={post} />
                    </div>

                    {(canEdit || canPublish || canArchive || canDelete) && (
                        <div className="flex flex-wrap gap-2 rounded-2xl border border-neutral-200 bg-white p-4">
                            {canEdit && (
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/posts/new?edit=${post.id}`)}
                                    data-testid="detail-edit-btn"
                                >
                                    <Pencil size={14} className="mr-2" /> Edit
                                </Button>
                            )}
                            {canPublish && (
                                <Button onClick={doPublish} className="nest-gradient text-white" data-testid="detail-publish-btn">
                                    <Send size={14} className="mr-2" /> Publish
                                </Button>
                            )}
                            {canArchive && (
                                <Button variant="outline" onClick={doArchive} data-testid="detail-archive-btn">
                                    <Archive size={14} className="mr-2" /> Archive
                                </Button>
                            )}
                            {canDelete && (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="destructive" data-testid="detail-delete-btn">
                                            <Trash2 size={14} className="mr-2" /> Delete
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This permanently removes the
                                                post. This cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={doDelete}
                                                className="bg-rose-600 text-white hover:bg-rose-700"
                                                data-testid="detail-delete-confirm"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


/** Coupon claim block (customer-only). Shows QR modal on success. */
function CouponClaimBlock({ post }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [claim, setClaim] = React.useState(null);
    const [modal, setModal] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const enabled = post?.details?.coupon_enabled;
    const isCustomer = user?.role === "CUSTOMER";

    const doClaim = async () => {
        if (!user) {
            toast("Sign in as a customer to claim coupons", {
                action: { label: "Sign in", onClick: () => nav("/customer/login") },
            });
            return;
        }
        setBusy(true);
        try {
            const r = await api.post(`/posts/${post.id}/coupons/claim`, {});
            setClaim({
                code: r.data.code,
                expires_at: r.data.expires_at,
                discount_snapshot: r.data.discount_snapshot,
                post_title: post.title,
                business_name: post.business_name,
            });
            setModal(true);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally { setBusy(false); }
    };

    // Phase 9c — auto-open when arriving via ?claim=1 (from PostCard claim chip).
    React.useEffect(() => {
        const usp = new URLSearchParams(window.location.search);
        if (usp.get("claim") === "1" && enabled && isCustomer && !claim && !busy) {
            doClaim();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, isCustomer]);

    // Merchants/admin should not see this CTA (checks AFTER hooks per rules-of-hooks)
    if (!enabled) return null;
    if (user && !isCustomer) return null;

    return (
        <div className="mt-4">
            <button
                onClick={doClaim}
                disabled={busy}
                data-testid="claim-coupon-btn"
                className="flex h-12 w-full items-center justify-center rounded-xl border-2 border-dashed border-orange-500 bg-orange-50 text-base font-bold text-orange-700 transition hover:bg-orange-100 disabled:opacity-60"
            >
                <TicketCheck size={18} className="mr-2" />
                {busy ? "Claiming…" : claim ? "View my coupon" : "Claim Coupon"}
            </button>
            <QRCodeModal open={modal} onOpenChange={setModal} claim={claim} />
        </div>
    );
}

// errorMessage helper (imported inline to avoid re-import block edits)

// ---------------------------------------------------------------------------
// Phase 15c — Redesigned OFFER detail view. Only rendered for type=OFFER.
// Full-width hero with a rotated red discount badge, warm orange shop strip,
// bold price row with "You save", info chips, urgency clock, and the same
// existing CouponClaimBlock at the bottom.
// ---------------------------------------------------------------------------

const humanizeSlug = (s) =>
    (s || "")
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());

const inrShort = (n) =>
    typeof n === "number" && !Number.isNaN(n) ? `₹${Math.round(n).toLocaleString("en-IN")}` : null;

function OfferDetailView({ post, onBack, onVisitShop, adminActions }) {
    const details = post.details || {};
    const priceAfter = typeof details.price_after === "number" ? details.price_after : null;
    const priceBefore = typeof details.price_before === "number" ? details.price_before : null;

    // Prefer stored discount_percent; fall back to a computed one.
    let discount = typeof details.discount_percent === "number" ? details.discount_percent : null;
    if (discount == null && priceAfter != null && priceBefore != null && priceBefore > 0 && priceAfter < priceBefore) {
        discount = Math.round((1 - priceAfter / priceBefore) * 100);
    }
    const saving = priceBefore != null && priceAfter != null && priceBefore > priceAfter ? priceBefore - priceAfter : null;

    const isOnline = String(details.business_type || "").toUpperCase() === "ONLINE";
    const shopInitial = (post.business_name || "?").trim().charAt(0).toUpperCase();
    const category = post.category_slug ? humanizeSlug(post.category_slug) : null;
    const district = post.district_slug ? humanizeSlug(post.district_slug) : null;
    const tags = Array.isArray(details.tags) ? details.tags.slice(0, 3) : [];

    const gallery = Array.isArray(details.gallery) ? details.gallery.filter(Boolean).slice(0, 6) : [];

    // Combined hero-carousel image list: poster first, then gallery, de-duped.
    const heroImages = (() => {
        const list = [];
        const seen = new Set();
        const push = (u) => {
            if (!u) return;
            const key = String(u).trim();
            if (!key || seen.has(key)) return;
            seen.add(key);
            list.push(key);
        };
        push(post.poster_image_url);
        gallery.forEach(push);
        return list;
    })();

    const onShare = async () => {
        const url = typeof window !== "undefined" ? window.location.href : "";
        try {
            if (navigator.share) {
                await navigator.share({ title: post.title, url });
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(url);
                toast.success("Link copied");
            }
        } catch { /* user cancelled */ }
    };

    return (
        <div className="min-h-screen bg-white pb-32" data-testid="post-detail-page">
            {/* Back / Share row */}
            <div className="mx-auto flex max-w-3xl items-center justify-between px-4 pt-4 md:px-6">
                <button
                    onClick={onBack}
                    className="inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1.5 text-sm font-semibold text-neutral-700 shadow-sm hover:text-neutral-900"
                    data-testid="detail-back-btn"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                <button
                    onClick={onShare}
                    className="inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1.5 text-sm font-semibold text-neutral-700 shadow-sm hover:text-neutral-900"
                    aria-label="Share"
                    data-testid="offer-share-btn"
                >
                    <Share2 size={14} /> Share
                </button>
            </div>

            {/* Hero */}
            <div className="mx-auto mt-3 max-w-3xl px-0 md:px-6">
                <div className="relative overflow-hidden rounded-b-3xl md:rounded-3xl">
                    <div className="relative aspect-[4/3] w-full bg-neutral-100 sm:aspect-video">
                        {heroImages.length > 0 ? (
                            <OfferHeroCarousel
                                images={heroImages}
                                title={post.title}
                                testidPrefix="offer-gallery"
                            />
                        ) : (
                            <div
                                className="flex h-full w-full items-center justify-center bg-neutral-100 font-heading text-5xl font-extrabold text-[var(--brand-orange)]"
                                data-testid="offer-hero-image"
                            >
                                {shopInitial}
                            </div>
                        )}
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent" />

                        {/* Discount badge — rotated red */}
                        {discount != null && discount > 0 && (
                            <div
                                className="absolute right-4 top-4 rotate-[-6deg] rounded-2xl bg-red-600 px-4 py-2 text-white shadow-lg"
                                style={{ transformOrigin: "top right" }}
                                data-testid="offer-discount-badge"
                            >
                                <div className="font-heading text-2xl font-black leading-none">{discount}% OFF</div>
                            </div>
                        )}

                        {/* Heart favorite — top-left */}
                        <div className="absolute left-4 top-4">
                            <HeartButton post={post} size={20} className="!bg-white/95 !px-2.5 !py-2 shadow-md" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Shop banner — orange gradient strip */}
            <div
                className="mx-auto mt-4 flex max-w-3xl items-center gap-3 bg-gradient-to-r from-[#FFF3E6] via-[#FFEBD5] to-[#FFF3E6] px-5 py-4 md:mt-4 md:rounded-2xl md:px-6"
                data-testid="offer-shop-banner"
            >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-orange-300 bg-orange-100 font-heading text-lg font-extrabold text-[var(--brand-orange)]">
                    {shopInitial}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-heading text-lg font-bold text-black" data-testid="offer-shop-name">
                        {post.business_name || "Shop"}
                    </div>
                    {district && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin size={12} /> {district}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onVisitShop}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--brand-orange)] px-3 py-1.5 font-heading text-xs font-semibold text-white hover:opacity-90"
                    data-testid="offer-visit-shop-btn"
                >
                    Visit Shop
                </button>
            </div>

            {/* Body */}
            <div className="mx-auto max-w-3xl bg-white px-5 py-6 md:px-6 md:py-8">
                <h1 className="font-heading text-2xl font-bold leading-tight text-black md:text-3xl" data-testid="detail-title">
                    {post.title}
                </h1>

                {/* Price row */}
                {(priceAfter != null || priceBefore != null) && (
                    <div className="mt-4">
                        <div className="flex flex-wrap items-baseline gap-3">
                            {priceAfter != null && (
                                <span
                                    className="font-heading text-3xl font-extrabold text-[#FF6B00]"
                                    data-testid="offer-price-after"
                                >
                                    {inrShort(priceAfter)}
                                </span>
                            )}
                            {priceBefore != null && priceBefore > (priceAfter ?? 0) && (
                                <span
                                    className="font-body text-lg text-gray-400 line-through"
                                    data-testid="offer-price-before"
                                >
                                    {inrShort(priceBefore)}
                                </span>
                            )}
                        </div>
                        {saving != null && (
                            <div className="mt-1 font-heading text-sm font-semibold text-emerald-600">
                                You save {inrShort(saving)}
                            </div>
                        )}
                        {discount != null && discount >= 30 && category && (
                            <div className="mt-1 font-heading text-xs font-semibold text-[var(--brand-orange)]">
                                Great deal — save big on {category}
                            </div>
                        )}
                    </div>
                )}

                {/* Description */}
                {post.description && (
                    <p className="mt-5 whitespace-pre-line font-body text-base leading-relaxed text-gray-700">
                        {post.description}
                    </p>
                )}

                {/* Gallery (if provided) */}
                {gallery.length > 0 && (
                    <div className="mt-6 grid grid-cols-3 gap-2" data-testid="offer-gallery">
                        {gallery.map((g, i) => (
                            <img
                                key={i}
                                src={publicAssetUrl(g)}
                                alt={`${post.title} ${i + 1}`}
                                className="aspect-square w-full rounded-xl object-cover"
                            />
                        ))}
                    </div>
                )}

                {/* Info chips */}
                <div className="mt-6 flex flex-wrap gap-2">
                    {category && (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 font-body text-sm font-medium text-blue-700"
                            data-testid="offer-category-chip"
                        >
                            <Tag size={12} /> {category}
                        </span>
                    )}
                    {isOnline ? (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 font-body text-sm font-medium text-purple-700"
                            data-testid="offer-online-chip"
                        >
                            <Globe size={12} /> Online
                        </span>
                    ) : (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 font-body text-sm font-medium text-emerald-700"
                            data-testid="offer-offline-chip"
                        >
                            <Store size={12} /> Offline
                        </span>
                    )}
                    {tags.map((t, i) => (
                        <span
                            key={`${t}-${i}`}
                            className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 font-body text-sm font-medium text-gray-700"
                        >
                            {t}
                        </span>
                    ))}
                </div>

                {/* Live countdown timer — ticks every second, tiered styling */}
                {post.valid_to && (
                    <CountdownTimer
                        expiresAt={post.valid_to}
                        categoryLabel={category}
                    />
                )}

                {/* Restaurant delivery buttons — Zomato / Swiggy (only when configured) */}
                {(details.delivery_zomato_url || details.delivery_swiggy_url) && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="offer-delivery-buttons">
                        {details.delivery_zomato_url && (
                            <a
                                href={details.delivery_zomato_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackEvent("post_view", { source: "delivery_click", platform: "zomato", post_id: post.id })}
                                data-testid="offer-order-zomato-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-[#E23744] px-4 py-2 font-heading text-sm font-semibold text-white shadow-sm hover:opacity-90"
                            >
                                Order on Zomato
                            </a>
                        )}
                        {details.delivery_swiggy_url && (
                            <a
                                href={details.delivery_swiggy_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackEvent("post_view", { source: "delivery_click", platform: "swiggy", post_id: post.id })}
                                data-testid="offer-order-swiggy-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-[#FC8019] px-4 py-2 font-heading text-sm font-semibold text-white shadow-sm hover:opacity-90"
                            >
                                Order on Swiggy
                            </a>
                        )}
                    </div>
                )}

                {/* Coupon claim (existing block — preserves the QR modal + analytics) */}
                <div className="mt-6" data-testid="offer-claim-slot">
                    <CouponClaimBlockWrapper post={post} />
                </div>

                {/* Owner / admin actions (if any) */}
                {(adminActions.canEdit || adminActions.canPublish || adminActions.canArchive || adminActions.canDelete) && (
                    <div className="mt-6 flex flex-wrap gap-2 rounded-2xl border border-neutral-200 bg-white p-4">
                        {adminActions.canEdit && (
                            <Button variant="outline" onClick={adminActions.onEdit} data-testid="detail-edit-btn">
                                <Pencil size={14} className="mr-2" /> Edit
                            </Button>
                        )}
                        {adminActions.canPublish && (
                            <Button onClick={adminActions.doPublish} className="nest-gradient text-white" data-testid="detail-publish-btn">
                                <Send size={14} className="mr-2" /> Publish
                            </Button>
                        )}
                        {adminActions.canArchive && (
                            <Button variant="outline" onClick={adminActions.doArchive} data-testid="detail-archive-btn">
                                <Archive size={14} className="mr-2" /> Archive
                            </Button>
                        )}
                        {adminActions.canDelete && (
                            <Button variant="destructive" onClick={adminActions.doDelete} data-testid="detail-delete-btn">
                                <Trash2 size={14} className="mr-2" /> Delete
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Small wrapper so we can reuse the existing CouponClaimBlock — same coupon
// flow, same QR modal, same analytics — without duplicating any logic here.
function CouponClaimBlockWrapper({ post }) {
    return <CouponClaimBlock post={post} />;
}


/**
 * Live-ticking countdown for the OFFER detail urgency block.
 * Uses a single `setInterval(1000)` while the component is mounted; cleans up
 * on unmount and when `expiresAt` changes. All math is derived from
 * `new Date(expiresAt) - new Date()` so timezones are respected via the
 * runtime's local-time interpretation of the ISO string.
 */
function CountdownTimer({ expiresAt, categoryLabel }) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!expiresAt) return undefined;
        setNow(Date.now()); // sync immediately on prop change
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [expiresAt]);

    const end = new Date(expiresAt).getTime();
    if (Number.isNaN(end)) return null;
    const msLeft = end - now;

    // --- Expired ---
    if (msLeft <= 0) {
        return (
            <div
                className="mt-6 flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-100 px-4 py-3 text-neutral-500"
                data-testid="offer-countdown-timer"
                data-state="expired"
            >
                <Clock size={16} className="text-neutral-400" />
                <span className="font-heading text-sm font-semibold">This offer has ended</span>
            </div>
        );
    }

    const totalSeconds = Math.floor(msLeft / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Pick tier from remaining time.
    // > 24h  → amber default
    // 12-24h → amber-strong
    // 1-12h  → red
    // < 1h   → red-pulse (uses `animate-pulse` on the whole block)
    let tier;
    if (msLeft > 24 * 60 * 60 * 1000) tier = "amber";
    else if (msLeft > 12 * 60 * 60 * 1000) tier = "amber-strong";
    else if (msLeft > 60 * 60 * 1000) tier = "red";
    else tier = "red-pulse";

    const containerClass =
        tier === "amber"
            ? "border border-amber-200 bg-amber-50 text-amber-800"
            : tier === "amber-strong"
                ? "border border-amber-300 bg-amber-100 text-amber-900"
                : tier === "red"
                    ? "border border-red-200 bg-red-50 text-red-700"
                    : "border border-red-300 bg-red-50 text-red-700 animate-pulse";

    const iconClass = tier.startsWith("red") ? "text-red-600" : "text-amber-600";
    const showAlarmEmoji = msLeft <= 24 * 60 * 60 * 1000;

    // Build the tiered text.
    let label;
    if (days > 0) {
        label = `${days}d ${hours}h ${minutes}m remaining`;
    } else if (hours > 0) {
        label = `${hours}h ${minutes}m ${String(seconds).padStart(2, "0")}s remaining`;
    } else if (minutes > 0) {
        label = `${minutes}m ${String(seconds).padStart(2, "0")}s remaining`;
    } else {
        label = `${seconds}s remaining`;
    }

    return (
        <div
            className={"mt-6 flex items-center gap-2 rounded-xl px-4 py-3 " + containerClass}
            data-testid="offer-countdown-timer"
            data-state={tier}
        >
            <Clock size={16} className={iconClass + (tier === "red-pulse" ? " animate-pulse" : "") } />
            <span className="font-heading text-sm font-semibold tabular-nums">
                {showAlarmEmoji ? "⏰ " : ""}{label}
            </span>
        </div>
    );
}

